/**
 * FULLER ARCHIVE — Hero Carousel (v1.1.60)
 * Clean implementation:
 * - Touch events for mobile (touchstart/touchend) — no preventDefault needed
 * - Pointer events for desktop mouse drag
 * - Arrow buttons + dots
 * - 6s auto-advance
 * - Ghost card stack
 */
(() => {
  function initCarousel(container) {
    const cards = Array.from(container.querySelectorAll('.fa-hero-carousel__card'));
    const wrapper = container.parentElement;
    const dotsContainer = wrapper.querySelector('.fa-hero-carousel__dots');
    const dots = dotsContainer ? Array.from(dotsContainer.querySelectorAll('.fa-hero-carousel__dot')) : [];

    if (cards.length === 0) return;

    let current = 0;
    let autoTimer = null;

    function updateCards() {
      cards.forEach((card) => {
        card.classList.remove('is-active', 'is-behind-1', 'is-behind-2');
        card.removeAttribute('aria-current');
        card.style.transform = '';
        card.style.transition = '';
      });
      cards[current].classList.add('is-active');
      cards[current].setAttribute('aria-current', 'true');
      if (cards.length > 1) cards[(current + 1) % cards.length].classList.add('is-behind-1');
      if (cards.length > 2) cards[(current + 2) % cards.length].classList.add('is-behind-2');
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
    }

    function goTo(index) {
      current = (index + cards.length) % cards.length;
      updateCards();
    }
    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAuto() {
      clearInterval(autoTimer);
      if (cards.length > 1) autoTimer = setInterval(next, 6000);
    }
    function stopAuto() { clearInterval(autoTimer); }

    // Initial render
    updateCards();
    startAuto();

    // Dots
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { stopAuto(); goTo(i); startAuto(); });
    });

    // Arrow buttons
    const prevBtn = wrapper.querySelector('[data-carousel-prev]');
    const nextBtn = wrapper.querySelector('[data-carousel-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => { stopAuto(); prev(); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stopAuto(); next(); startAuto(); });

    // Block native image drag
    container.addEventListener('dragstart', (e) => e.preventDefault());

    // ── TOUCH (mobile) ──
    let touchStartX = 0;
    let touchStartY = 0;

    container.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      stopAuto();
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      const dx = touchStartX - e.changedTouches[0].clientX;
      const dy = touchStartY - e.changedTouches[0].clientY;
      // Only swipe if horizontal movement dominates
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
        dx > 0 ? next() : prev();
      }
      startAuto();
    }, { passive: true });

    // ── MOUSE DRAG (desktop) ──
    let mouseStartX = 0;
    let mouseCurrentX = 0;
    let isMouseDown = false;
    let mouseMoved = false;

    container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // left button only
      isMouseDown = true;
      mouseMoved = false;
      mouseStartX = e.clientX;
      mouseCurrentX = e.clientX;
      container.style.cursor = 'grabbing';
      stopAuto();
      e.preventDefault(); // prevent text selection on desktop
    });

    document.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;
      mouseCurrentX = e.clientX;
      const diff = mouseCurrentX - mouseStartX;
      if (Math.abs(diff) > 5) mouseMoved = true;

      if (mouseMoved) {
        const activeCard = cards[current];
        if (activeCard) {
          const tilt = Math.min(Math.max(diff * 0.04, -8), 8);
          const tx = Math.min(Math.max(diff * 0.15, -40), 40);
          activeCard.style.transition = 'none';
          activeCard.style.transform = `translateX(${tx}px) rotate(${tilt}deg)`;
        }
      }
    });

    document.addEventListener('mouseup', (e) => {
      if (!isMouseDown) return;
      isMouseDown = false;
      container.style.cursor = 'grab';

      const diff = mouseStartX - mouseCurrentX;
      const activeCard = cards[current];
      if (activeCard) {
        activeCard.style.transition = '';
        activeCard.style.transform = '';
      }

      if (mouseMoved && Math.abs(diff) > 30) {
        diff > 0 ? next() : prev();
      }
      mouseMoved = false;
      startAuto();
    });

    // Prevent link click after drag
    container.addEventListener('click', (e) => {
      if (mouseMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Keyboard
    container.setAttribute('tabindex', '0');
    container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { stopAuto(); next(); startAuto(); }
      if (e.key === 'ArrowLeft') { stopAuto(); prev(); startAuto(); }
    });

    // Horizontal scroll wheel only
    let wheelCooldown = false;
    container.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) * 1.5) return;
      if (Math.abs(e.deltaX) < 10) return;
      if (wheelCooldown) return;
      e.preventDefault();
      wheelCooldown = true;
      stopAuto();
      e.deltaX > 0 ? next() : prev();
      setTimeout(() => { wheelCooldown = false; }, 800);
      startAuto();
    }, { passive: false });

    // Pause on hover
    container.addEventListener('mouseenter', stopAuto);
    container.addEventListener('mouseleave', () => {
      if (!isMouseDown) startAuto();
    });
  }

  function init() {
    document.querySelectorAll('.fa-hero-carousel').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
